import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { Campaign } from '@/api/types';
import {
  CAMPAIGN_SEND_RAIL_LABELS,
  CAMPAIGN_TYPE_LABELS,
  MAX_RUNNING_CAMPAIGNS_PER_PROJECT,
} from '@marketing-hub/shared';
import { useProjectContext } from '@/project/ProjectContext';
import { useProjectRole } from '@/project/useProjectRole';
import { useToast } from '@/components/toast/ToastProvider';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import { CampaignQuickSendPanel } from '@/features/campaigns/CampaignQuickSendPanel';
import { CampaignReadinessPanel } from '@/features/campaigns/CampaignReadinessPanel';
import { CampaignStatusBadge } from '@/features/campaigns/CampaignStatusBadge';
import { formatSendWindow } from '@/features/campaigns/campaignUi';
import { labelCampaignPauseReason } from '@/features/campaigns/enrollmentLabels';
import campStyles from '@/features/campaigns/Campaigns.module.css';

type StatusFilter = 'all' | Campaign['status'];

export function CampaignHubPage() {
  const { selectedProject } = useProjectContext();
  const projectId = selectedProject?.id;
  const qc = useQueryClient();
  const toast = useToast();
  const { can: canProject } = useProjectRole(projectId);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const deleteCampaignM = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.push('Campagne verwijderd', 'success');
      void qc.invalidateQueries({ queryKey: ['campaigns', projectId] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  const campaignsQ = useQuery({
    queryKey: ['campaigns', projectId],
    enabled: Boolean(projectId),
    queryFn: () => apiFetch<{ campaigns: Campaign[] }>(`/api/campaigns?project_id=${projectId}`),
  });

  const campaigns = campaignsQ.data?.campaigns ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (!q) return true;
      const typeLabel =
        CAMPAIGN_TYPE_LABELS[c.type as keyof typeof CAMPAIGN_TYPE_LABELS] ?? c.type;
      return c.name.toLowerCase().includes(q) || typeLabel.toLowerCase().includes(q);
    });
  }, [campaigns, search, statusFilter]);

  if (!projectId) return null;
  if (campaignsQ.isLoading) return <LoadingState />;

  const running = campaigns.filter((c) => c.status === 'running').length;
  const draft = campaigns.filter((c) => c.status === 'draft').length;
  const enrolledTotal = campaigns.reduce((sum, c) => sum + (c.enrollments ?? 0), 0);

  return (
    <div className={campStyles.hubLayout}>
      <div>
        <div className={campStyles.summaryGrid}>
          <div className={campStyles.summaryCard}>
            <p className={campStyles.summaryLabel}>Campagnes</p>
            <p className={campStyles.summaryValue}>{campaigns.length}</p>
            <p className={campStyles.summaryHint}>{draft} concept</p>
          </div>
          <div className={campStyles.summaryCard}>
            <p className={campStyles.summaryLabel}>Actief</p>
            <p className={campStyles.summaryValue}>
              {running}
              <span className={campStyles.mutedInline}> / {MAX_RUNNING_CAMPAIGNS_PER_PROJECT}</span>
            </p>
            <p className={campStyles.summaryHint}>Parallelle running campagnes</p>
          </div>
          <div className={campStyles.summaryCard}>
            <p className={campStyles.summaryLabel}>Enrolled (totaal)</p>
            <p className={campStyles.summaryValue}>{enrolledTotal}</p>
            <p className={campStyles.summaryHint}>Over alle campagnes</p>
          </div>
          <div className={campStyles.summaryCard}>
            <p className={campStyles.summaryLabel}>Analytics</p>
            <p className={campStyles.summaryValue} style={{ fontSize: '1.125rem' }}>
              <Link to="/app/analytics#campagnes">Mail-KPI&apos;s →</Link>
            </p>
            <p className={campStyles.summaryHint}>Reply & funnel per campagne</p>
          </div>
        </div>

        <CampaignQuickSendPanel projectId={projectId} compact />

        <div className={campStyles.toolbar} style={{ marginBottom: 'var(--space-md)' }}>
          <div className={campStyles.toolbarFilters}>
            <input
              type="search"
              className={campStyles.searchInput}
              placeholder="Zoek op naam of type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Campagnes zoeken"
            />
            <select
              className={campStyles.filterSelect}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              aria-label="Filter op status"
            >
              <option value="all">Alle statussen</option>
              <option value="running">Actief</option>
              <option value="draft">Concept</option>
              <option value="paused">Gepauzeerd</option>
              <option value="completed">Afgerond</option>
            </select>
          </div>
          <Link to="/app/campagnes/nieuw">
            <Button type="button">Nieuwe campagne</Button>
          </Link>
        </div>

        <section className={campStyles.panel}>
          <div className={campStyles.panelHead}>
            <h2 className={campStyles.panelTitle}>Portfolio</h2>
            <span className={campStyles.metaChip}>
              {filtered.length} van {campaigns.length}
            </span>
          </div>
          {filtered.length === 0 ? (
            <div className={campStyles.emptyState}>
              <p className={campStyles.emptyTitle}>
                {campaigns.length === 0 ? 'Nog geen campagnes' : 'Geen resultaten'}
              </p>
              <p className={campStyles.emptyText}>
                {campaigns.length === 0
                  ? 'Maak een campagne via de wizard, koppel een doelgroep of lijst en een sequence uit de bibliotheek.'
                  : 'Pas zoekterm of filter aan.'}
              </p>
              {campaigns.length === 0 ? (
                <Link to="/app/campagnes/nieuw">
                  <Button type="button">Start wizard</Button>
                </Link>
              ) : null}
            </div>
          ) : (
            <div className={campStyles.tableWrap}>
              <table className={campStyles.dataTable}>
                <thead>
                  <tr>
                    <th>Campagne</th>
                    <th>Status</th>
                    <th>Enrolled</th>
                    <th>Venster</th>
                    <th aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const typeLabel =
                      CAMPAIGN_TYPE_LABELS[c.type as keyof typeof CAMPAIGN_TYPE_LABELS] ?? c.type;
                    const rail =
                      c.type !== 'broadcast'
                        ? CAMPAIGN_SEND_RAIL_LABELS[c.send_rail ?? 'smtp']
                        : 'Brevo broadcast';
                    return (
                      <tr key={c.id}>
                        <td>
                          <Link className={campStyles.cellTitle} to={`/app/campagnes/${c.id}`}>
                            {c.name}
                          </Link>
                          <div className={campStyles.cellMeta}>
                            {typeLabel} · {rail}
                            {c.status === 'paused' && c.pause_reason ? (
                              <> · {labelCampaignPauseReason(c.pause_reason)}</>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <CampaignStatusBadge status={c.status} />
                        </td>
                        <td>{c.enrollments ?? 0}</td>
                        <td>{formatSendWindow(c.send_hour_start, c.send_hour_end)}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                            <Link to={`/app/campagnes/${c.id}`}>
                              <Button type="button" variant="ghost">
                                Open
                              </Button>
                            </Link>
                            {c.status !== 'running' && c.status !== 'scheduled' && canProject('campaigns_draft') ? (
                              <Button
                                type="button"
                                variant="ghost"
                                disabled={deleteCampaignM.isPending}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Campagne "${c.name}" definitief verwijderen?`,
                                    )
                                  ) {
                                    deleteCampaignM.mutate(c.id);
                                  }
                                }}
                              >
                                Verwijder
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <div className={campStyles.sideStack}>
        <CampaignReadinessPanel projectId={projectId} />
        <section className={campStyles.panel}>
          <div className={campStyles.panelHead}>
            <h2 className={campStyles.panelTitle}>Snelkoppelingen</h2>
          </div>
          <div className={campStyles.panelBody}>
            <p className={campStyles.panelBodyMuted} style={{ padding: 0 }}>
              <Link to="/app/campagnes/bibliotheek">Bibliotheek</Link> — templates & sequences
              <br />
              <Link to="/app/doelgroepen">Doelgroepen</Link> — segmenten koppelen
              <br />
              <Link to="/app/deliverability">Deliverability</Link> — DNS & reputation
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
